import { useState } from 'react';
import {
  ArrowRight, Check, Star, Users, FileText, BarChart3,
  Sparkles, Shield, Zap, Calendar, Home, Mail,
  ChevronDown, ChevronUp, MessageCircle, Phone,
} from 'lucide-react';

/**
 * LandingPage — marketing page for trovva.ai
 *
 * Single-page with sections:
 *   Hero → Features → Pricing → Testimonials → FAQ → CTA → Footer
 *
 * Self-contained with inline styles. No external dependencies.
 */

const BRAND = {
  primary: '#1B5E54',
  primaryLight: '#2A7A6E',
  accent: '#E8913A',
  accentLight: '#F5A855',
  bg: '#FAFBFC',
  dark: '#0F1F1C',
  text: '#1A2B3C',
  textLight: '#64748B',
  card: '#FFFFFF',
  border: '#E2E8F0',
};

const FEATURES = [
  {
    icon: MessageCircle,
    title: 'Reminderly — Trovva Brain via Text',
    desc: 'Morning briefings, task reminders, and lead updates delivered via SMS or WhatsApp. Your agents manage their entire day without ever opening a laptop.',
    badge: 'Only in Trovva',
  },
  {
    icon: Phone,
    title: 'Voice AI — Talk to Your CRM',
    desc: '"Hey Trovva, what\'s on my plate today?" Agents call in, get briefed, add notes, and move deals forward — hands-free, while driving between showings.',
    badge: 'Only in Trovva',
  },
  {
    icon: Sparkles,
    title: 'AI That Actually Does the Work',
    desc: 'Not a chatbot. Trovva reads the full conversation history and writes follow-ups, listing descriptions, and drip emails that sound like your agent wrote them.',
  },
  {
    icon: Users,
    title: 'Leads Never Fall Through the Cracks',
    desc: 'Zillow, Facebook, email, and web leads land in one inbox. AI scores them, routes them instantly, and drip campaigns send from the agent\'s own Gmail — not a "noreply."',
  },
  {
    icon: FileText,
    title: 'Deals from Contract to Close',
    desc: 'Milestones, document checklists, compliance tracking, and e-signatures. Clients get a branded portal — no more "where are we in the process?" calls.',
  },
  {
    icon: BarChart3,
    title: 'Your Agents Run Themselves',
    desc: 'Upload your office policies once — the AI answers your agents\' questions 24/7. You only hear about the deals that genuinely need your expertise.',
    badge: 'Only in Trovva',
  },
  {
    icon: Calendar,
    title: 'Clients Book Their Own Showings',
    desc: 'Send a booking link, they pick a time. Confirmations, reminders, and feedback requests happen automatically. No more back-and-forth texts to schedule.',
  },
  {
    icon: Shield,
    title: 'Your Brand, Not Ours',
    desc: 'Your logo, your colors, your domain. Client portals, emails, and booking pages look like YOUR brokerage. Clients never see the word "Trovva."',
  },
];

const PRICING = [
  {
    name: 'Trovva One',
    price: '$149',
    period: '/month',
    desc: 'For solo brokers running their own book',
    features: [
      'Reminderly — manage your day from a text message',
      'Voice AI — call in, get briefed, hands-free',
      'AI writes your follow-ups, listings & emails',
      'Never lose a lead — every inquiry in one inbox',
      'Clients book their own showings from your link',
      'E-sign contracts in 30 seconds (10/mo included)',
      'Branded deal rooms that impress your clients',
      'Drip campaigns that send from YOUR Gmail',
    ],
    highlight: false,
  },
  {
    name: 'Trovva Team',
    price: '$129',
    period: '/seat/month',
    desc: 'For growing brokerages with 2–5 agents',
    features: [
      'Everything in Trovva One +',
      'See every deal, lead & dollar in real time',
      'Know which agents are producing — and who needs help',
      'Auto-route leads by zip, round-robin, or top performer',
      'Your logo, your colors — clients never see "Trovva"',
      'Onboard new agents in under 60 seconds',
      'Connect 3,000+ apps via Zapier',
      'Priority support — real humans, same day',
    ],
    highlight: true,
  },
  {
    name: 'Trovva Pro',
    price: '$99',
    period: '/seat/month',
    desc: 'For established brokerages with 6+ agents',
    features: [
      'Everything in Trovva Team +',
      'Volume pricing — your best per-seat rate',
      'Recruiting CRM to grow your roster',
      'Agent attrition risk scoring & alerts',
      'Advanced reporting & production analytics',
      'Dedicated onboarding & account manager',
      'Custom integrations & API access',
      'White-label everything — your brand only',
    ],
    highlight: false,
  },
];

const TESTIMONIALS = [
  {
    name: 'Sarah Martinez',
    role: 'Broker, Garden State Realty',
    text: 'We switched from three different tools to Trovva. My agents actually use it because it\'s not bloated with features nobody needs.',
    stars: 5,
  },
  {
    name: 'David Kim',
    role: 'Agent, Summit Properties',
    text: 'The AI follow-up writer saves me an hour a day. It reads the conversation history and drafts exactly what I would have written.',
    stars: 5,
  },
  {
    name: 'Jennifer Brown',
    role: 'Broker/Owner, JB Real Estate',
    text: 'Finally a platform built for indie brokerages, not mega-teams. The pricing is fair and the client portals impress my sellers.',
    stars: 5,
  },
];

const FAQ = [
  {
    q: 'Who is Trovva built for?',
    a: 'Independent NJ brokerages — from solo brokers to teams of 15+. Whether you\'re running your own book or managing a growing office, Trovva replaces the 5 tools you\'re stitching together today.',
  },
  {
    q: 'Can I try it before buying?',
    a: 'Yes — every account starts with a 14-day free trial. No credit card required. You can also explore the platform in demo mode without signing up.',
  },
  {
    q: 'Do my agents need separate logins?',
    a: 'Yes, each agent gets their own account with role-based access. Brokers invite agents by email, and they\'re set up in under a minute.',
  },
  {
    q: 'What about MLS integration?',
    a: 'Trovva doesn\'t pull MLS data (agents already have that in Bright MLS). Instead, we focus on what happens after — CRM, transactions, and client communication.',
  },
  {
    q: 'How does e-signature work?',
    a: 'We use BoldSign for legally-binding e-signatures at $0.75/envelope. Each agent seat includes 10 envelopes/month, with overflow packs available.',
  },
  {
    q: 'Can I use my own branding?',
    a: 'Absolutely. Upload your logo, set your brand color, and optionally use a custom domain. Client portals, emails, and the login page all match your brand.',
  },
];

export default function LandingPage({ onGetStarted, onLegal }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [email, setEmail] = useState('');

  const handleCTA = () => {
    if (onGetStarted) onGetStarted(email);
  };

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: BRAND.text }}>
      {/* ── Nav ─────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${BRAND.border}`,
        padding: '0 24px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        maxWidth: 1200, margin: '0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, color: 'white', fontSize: 20,
          }}>T</div>
          <span style={{ fontSize: 20, fontWeight: 800, color: BRAND.primary }}>Trovva</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <a href="#features" style={{ color: BRAND.textLight, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>Features</a>
          <a href="#simplicity" style={{ color: BRAND.textLight, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>Why Trovva</a>
          <a href="#compare" style={{ color: BRAND.textLight, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>Compare</a>
          <a href="#pricing" style={{ color: BRAND.textLight, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>Pricing</a>
          <a href="#faq" style={{ color: BRAND.textLight, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>FAQ</a>
          <button
            onClick={handleCTA}
            style={{
              padding: '8px 20px', borderRadius: 8,
              background: BRAND.primary, color: 'white',
              border: 'none', fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────── */}
      <section style={{
        paddingTop: 140, paddingBottom: 80,
        textAlign: 'center',
        background: `linear-gradient(180deg, ${BRAND.bg} 0%, white 100%)`,
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>
          <div style={{
            display: 'inline-block', padding: '6px 16px', borderRadius: 20,
            background: `${BRAND.accent}15`, color: BRAND.accent,
            fontSize: 13, fontWeight: 600, marginBottom: 20,
          }}>
            Built for independent NJ brokerages
          </div>

          <h1 style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: 800,
            lineHeight: 1.1,
            margin: '0 0 20px',
            color: BRAND.dark,
          }}>
            The AI-native<br />
            <span style={{ color: BRAND.primary }}>Broker OS</span>
          </h1>

          <p style={{
            fontSize: 'clamp(16px, 2vw, 20px)',
            color: BRAND.textLight,
            lineHeight: 1.6,
            margin: '0 0 32px',
            maxWidth: 540,
            marginLeft: 'auto', marginRight: 'auto',
          }}>
            Deals don't wait. Neither should your Broker OS.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <input
              type="email"
              placeholder="Enter your work email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{
                padding: '12px 20px', borderRadius: 10,
                border: `1.5px solid ${BRAND.border}`,
                fontSize: 15, width: 280, outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              onClick={handleCTA}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 28px', borderRadius: 10,
                background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`,
                color: 'white', border: 'none',
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                boxShadow: `0 4px 14px ${BRAND.primary}40`,
              }}
            >
              Start Free Trial
              <ArrowRight size={18} />
            </button>
          </div>

          <p style={{ fontSize: 13, color: BRAND.textLight, marginTop: 12 }}>
            14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* ── Social proof bar ────────────────────────────── */}
      <section style={{
        padding: '24px 0',
        borderTop: `1px solid ${BRAND.border}`,
        borderBottom: `1px solid ${BRAND.border}`,
        background: 'white',
      }}>
        <div style={{
          maxWidth: 800, margin: '0 auto', padding: '0 24px',
          display: 'flex', justifyContent: 'center', gap: 40, flexWrap: 'wrap',
          color: BRAND.textLight, fontSize: 14,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={16} color={BRAND.primary} /> Trovva Brain (SMS + WhatsApp)
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={16} color={BRAND.primary} /> Voice AI — Hands-Free CRM
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={16} color={BRAND.primary} /> Zillow + Facebook Leads
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={16} color={BRAND.primary} /> Gmail & Outlook
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={16} color={BRAND.primary} /> E-Signatures Built In
          </span>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────── */}
      <section id="features" style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, margin: '0 0 12px', color: BRAND.dark }}>
              What makes Trovva different
            </h2>
            <p style={{ fontSize: 16, color: BRAND.textLight, maxWidth: 560, margin: '0 auto' }}>
              Other CRMs give agents a dashboard and hope they use it. Trovva gives them Trovva Brain — AI that works by text, voice, and web — so nothing falls through the cracks.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 24,
          }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} style={{
                  padding: 24, borderRadius: 14,
                  border: `1px solid ${BRAND.border}`,
                  background: BRAND.card,
                  transition: 'box-shadow 0.2s',
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: `${BRAND.primary}10`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 14,
                  }}>
                    <Icon size={22} color={BRAND.primary} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
                    <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: BRAND.dark }}>{f.title}</h3>
                    {f.badge && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: `${BRAND.accent}18`, color: BRAND.accent, whiteSpace: 'nowrap',
                      }}>{f.badge}</span>
                    )}
                  </div>
                  <p style={{ fontSize: 14, color: BRAND.textLight, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Simplicity Section ──────────────────────────── */}
      <section id="simplicity" style={{ padding: '80px 24px', background: BRAND.bg }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, margin: '0 0 12px', color: BRAND.dark }}>
              Powerful doesn't have to mean complicated
            </h2>
            <p style={{ fontSize: 16, color: BRAND.textLight, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
              Most brokerage platforms were designed by engineers for enterprise teams.
              Trovva was designed by a broker, for brokers — so your agents actually use it.
            </p>
          </div>

          {/* Two-column: Other Platforms vs Trovva */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 24, marginBottom: 40,
          }}>
            {/* Other platforms */}
            <div style={{
              padding: 28, borderRadius: 14, background: 'white',
              border: `1px solid ${BRAND.border}`,
            }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#dc2626',
                textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16,
              }}>
                What you're used to
              </div>
              {[
                '2-hour onboarding sessions nobody remembers',
                'Agents need a training manual to add a lead',
                '47 menu items, 12 settings pages',
                'Pay extra for "advanced" features you already need',
                '"CRM adoption" is a permanent agenda item',
                'Agents use it for a week, then go back to spreadsheets',
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
                  fontSize: 14, color: BRAND.textLight, lineHeight: 1.5,
                }}>
                  <span style={{ color: '#dc2626', fontSize: 16, lineHeight: 1, marginTop: 1 }}>✕</span>
                  {item}
                </div>
              ))}
            </div>

            {/* Trovva */}
            <div style={{
              padding: 28, borderRadius: 14,
              background: `linear-gradient(135deg, ${BRAND.dark}, #1a3a34)`,
              border: 'none',
            }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: BRAND.accent,
                textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16,
              }}>
                How Trovva works
              </div>
              {[
                'Agent signs in, sees their leads and tasks — that\'s it',
                'Add a lead in 3 taps. Move a deal with drag-and-drop',
                'Simple Mode hides advanced features until you need them',
                'Everything included — no feature gates, no upgrade traps',
                'Agents manage their day by text — no login needed',
                'If they can text, they can use Trovva',
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
                  fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5,
                }}>
                  <span style={{ color: BRAND.accent, fontSize: 16, lineHeight: 1, marginTop: 1 }}>✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom stat line */}
          <div style={{
            textAlign: 'center', padding: '20px 24px', borderRadius: 12,
            background: `${BRAND.primary}08`, border: `1px solid ${BRAND.primary}15`,
          }}>
            <p style={{ fontSize: 15, color: BRAND.text, margin: 0, fontWeight: 500 }}>
              The average CRM has a <strong style={{ color: '#dc2626' }}>23% adoption rate</strong> after 90 days.
              Trovva was built so that number is <strong style={{ color: BRAND.primary }}>100%</strong> — because
              your agents work by text and voice — they're productive from day one.
            </p>
          </div>
        </div>
      </section>

      {/* ── Why Switch / Wedge Section ─────────────────── */}
      <section id="compare" style={{ padding: '80px 24px', background: BRAND.bg }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, margin: '0 0 12px', color: BRAND.dark }}>
              Replace your entire tech stack
            </h2>
            <p style={{ fontSize: 16, color: BRAND.textLight, maxWidth: 520, margin: '0 auto' }}>
              Stop paying for 5 tools that don't talk to each other. Trovva does what all of them do — and things none of them can.
            </p>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
          }}>
            {[
              { old: 'Follow Up Boss', cost: '$69-499/mo', trovva: 'Full CRM with AI scoring, drip campaigns, smart lists — plus text and voice AI that FUB doesn\'t have' },
              { old: 'dotloop / SkySlope', cost: '$32-340/mo', trovva: 'E-signatures, deal tracking, compliance checklists — built in' },
              { old: 'Calendly', cost: '$10-20/user/mo', trovva: 'Showing scheduler with client self-booking and feedback' },
              { old: 'Mailchimp / Constant Contact', cost: '$50+/mo', trovva: 'Email campaigns from agent\'s own Gmail — not a generic sender' },
              { old: 'ChatGPT / copy-paste AI', cost: '$20/mo', trovva: 'AI trained on your deals that writes in your agent\'s voice' },
              { old: 'Nothing (Text/Voice AI)', cost: 'No alternative exists', trovva: 'Reminderly text + Voice AI — manage your brokerage hands-free' },
            ].map((item, i) => (
              <div key={i} style={{
                padding: 20, borderRadius: 12, background: 'white',
                border: `1px solid ${BRAND.border}`,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontSize: 14, fontWeight: 600, color: BRAND.dark,
                    textDecoration: 'line-through', textDecorationColor: '#ef4444',
                    opacity: 0.6,
                  }}>{item.old}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: item.cost === 'No alternative exists' ? `${BRAND.accent}15` : '#fef2f2',
                    color: item.cost === 'No alternative exists' ? BRAND.accent : '#dc2626',
                  }}>{item.cost}</span>
                </div>
                <div style={{ fontSize: 13, color: BRAND.textLight, lineHeight: 1.5 }}>
                  <span style={{ color: BRAND.primary, fontWeight: 700 }}>Trovva: </span>
                  {item.trovva}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────── */}
      <section id="pricing" style={{ padding: '80px 24px', background: BRAND.bg }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, margin: '0 0 12px', color: BRAND.dark }}>
              Simple, honest pricing
            </h2>
            <p style={{ fontSize: 16, color: BRAND.textLight }}>
              No hidden fees. No per-transaction charges. No annual contracts.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 24,
          }}>
            {PRICING.map((plan, i) => (
              <div key={i} style={{
                padding: 32, borderRadius: 16,
                background: plan.highlight ? BRAND.dark : 'white',
                border: plan.highlight ? 'none' : `1px solid ${BRAND.border}`,
                boxShadow: plan.highlight ? '0 20px 40px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                position: 'relative',
              }}>
                {plan.highlight && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    padding: '4px 16px', borderRadius: 20,
                    background: BRAND.accent, color: 'white',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    Most Popular
                  </div>
                )}

                <h3 style={{
                  fontSize: 18, fontWeight: 700, margin: '0 0 4px',
                  color: plan.highlight ? 'white' : BRAND.dark,
                }}>{plan.name}</h3>
                <p style={{
                  fontSize: 13, margin: '0 0 16px',
                  color: plan.highlight ? 'rgba(255,255,255,0.6)' : BRAND.textLight,
                }}>{plan.desc}</p>

                <div style={{ marginBottom: 20 }}>
                  <span style={{
                    fontSize: 40, fontWeight: 800,
                    color: plan.highlight ? 'white' : BRAND.dark,
                  }}>{plan.price}</span>
                  <span style={{
                    fontSize: 15,
                    color: plan.highlight ? 'rgba(255,255,255,0.6)' : BRAND.textLight,
                  }}>{plan.period}</span>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                  {plan.features.map((f, j) => (
                    <li key={j} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 0',
                      fontSize: 14,
                      color: plan.highlight ? 'rgba(255,255,255,0.85)' : BRAND.text,
                    }}>
                      <Check size={16} color={plan.highlight ? BRAND.accentLight : BRAND.primary} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleCTA}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 10,
                    background: plan.highlight
                      ? `linear-gradient(135deg, ${BRAND.accent}, ${BRAND.accentLight})`
                      : BRAND.primary,
                    color: 'white', border: 'none',
                    fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Start Free Trial
                </button>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: BRAND.textLight }}>
            E-signature overflow packs: 10/$15 &middot; 30/$35 &middot; 100/$99
          </p>
        </div>
      </section>

      {/* ── Testimonials ────────────────────────────────── */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: 32, fontWeight: 800, margin: '0 0 40px', color: BRAND.dark }}>
            Brokers love Trovva
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
          }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{
                padding: 24, borderRadius: 14,
                border: `1px solid ${BRAND.border}`,
                background: BRAND.card,
              }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} size={16} color={BRAND.accent} fill={BRAND.accent} />
                  ))}
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: BRAND.text, margin: '0 0 16px' }}>
                  "{t.text}"
                </p>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.dark }}>{t.name}</div>
                  <div style={{ fontSize: 13, color: BRAND.textLight }}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────── */}
      <section id="faq" style={{ padding: '80px 24px', background: BRAND.bg }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: 32, fontWeight: 800, margin: '0 0 40px', color: BRAND.dark }}>
            Frequently asked questions
          </h2>

          {FAQ.map((item, i) => (
            <div key={i} style={{
              borderBottom: `1px solid ${BRAND.border}`,
              padding: '16px 0',
            }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{
                  width: '100%', textAlign: 'left',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 0, fontSize: 16, fontWeight: 600,
                  color: BRAND.dark, fontFamily: 'inherit',
                }}
              >
                {item.q}
                {openFaq === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {openFaq === i && (
                <p style={{
                  fontSize: 14, lineHeight: 1.6, color: BRAND.textLight,
                  margin: '10px 0 0', paddingRight: 24,
                }}>
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────── */}
      <section style={{
        padding: '80px 24px',
        background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.primary} 100%)`,
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <h2 style={{ fontSize: 32, fontWeight: 800, color: 'white', margin: '0 0 16px' }}>
            Ready to modernize your brokerage?
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: '0 0 32px' }}>
            Give your agents a platform they'll actually use — and give yourself the freedom to focus on what matters most.
          </p>
          <button
            onClick={handleCTA}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 32px', borderRadius: 12,
              background: BRAND.accent, color: 'white',
              border: 'none', fontSize: 16, fontWeight: 700,
              cursor: 'pointer',
              boxShadow: `0 4px 20px ${BRAND.accent}50`,
            }}
          >
            Start Your Free Trial
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────── */}
      <footer style={{
        padding: '40px 24px',
        background: BRAND.dark,
        borderTop: `1px solid rgba(255,255,255,0.1)`,
      }}>
        <div style={{
          maxWidth: 1000, margin: '0 auto',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, color: 'white', fontSize: 15,
            }}>T</div>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>Trovva.ai</span>
          </div>

          <div style={{ display: 'flex', gap: 20 }}>
            <a href="mailto:hello@trovva.ai" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: 13 }}>
              <Mail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> hello@trovva.ai
            </a>
            <a href="tel:+19735550100" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: 13 }}>
              <Phone size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> (973) 555-0100
            </a>
          </div>

          <div style={{ width: '100%', textAlign: 'center', marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <button
                onClick={() => onLegal?.('terms')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecoration: 'underline',
                }}
              >Terms of Service</button>
              <button
                onClick={() => onLegal?.('privacy')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecoration: 'underline',
                }}
              >Privacy Policy</button>
            </div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', margin: 0 }}>
              &copy; {new Date().getFullYear()} Trovva Inc. All rights reserved. Built in New Jersey.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
